import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export interface ProviderInfo {
  providerId: string;
  providerName: string;
  monthlyPriceCents: number;
  patientPortionCents: number;
  subsidyPercent: number;
  currency: string;
}

export interface IdentityInfo {
  firstName: string;
  lastName?: string;
  dob?: string;    // YYYY-MM-DD
  nhi?: string;    // optional, preferred match field when present
  /** On a claim link: the email the clinic has on file (where the link went). */
  email: string;
  acceptedTermsAt?: string; // ISO timestamp
}

export interface ClaimResult {
  entitlementId: string;
  providerName: string;
  patientPortionCents: number;
  noPaymentRequired: boolean;
}

interface ClaimState {
  /** The single-use link from the email (Tess re-scan 3): required to claim. */
  claimToken: string | null;
  providerCode: string | null;
  provider: ProviderInfo | null;
  identity: IdentityInfo | null;
  result: ClaimResult | null;
}

interface ClaimContextValue extends ClaimState {
  setProvider(code: string, info: ProviderInfo): void;
  setIdentity(info: IdentityInfo): void;
  setResult(result: ClaimResult): void;
  /** Opened from the emailed link: the link's provider and member replace anything typed. */
  setFromLink(token: string, provider: ProviderInfo, identity: IdentityInfo): void;
  reset(): void;
}

const STORAGE_KEY = '@dj_claim_state_v3';

const initial: ClaimState = {
  claimToken: null,
  providerCode: null,
  provider: null,
  identity: null,
  result: null,
};

const Ctx = createContext<ClaimContextValue | null>(null);

/**
 * Survives back/forward navigation + page refresh by mirroring to
 * sessionStorage. Deliberately session-scoped — closing the tab wipes
 * the in-progress claim, which is the right default for a payment flow.
 */
export function ClaimProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ClaimState>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? { ...initial, ...JSON.parse(raw) } : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage quota / private mode — silent fallback; in-memory only.
    }
  }, [state]);

  const value: ClaimContextValue = {
    ...state,
    setProvider(code, info) {
      setState((s) => ({ ...s, providerCode: code, provider: info }));
    },
    setIdentity(info) {
      setState((s) => ({ ...s, identity: info }));
    },
    setResult(result) {
      setState((s) => ({ ...s, result }));
    },
    setFromLink(token, provider, identity) {
      setState({ ...initial, claimToken: token, providerCode: null, provider, identity });
    },
    reset() {
      setState(initial);
      try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useClaim() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useClaim must be used inside <ClaimProvider>');
  return v;
}
